import { releaseCanvas } from 'mixtiles-web-common/services/canvasService'
import { logger } from '../services/logger'
import {
  calculateSmartCropFromUrl,
  ExternalError,
} from '../services/SmartCrop/smartCrop'
import { extractThumbDimensionsFromUrl } from '../components/PhotoWall/cloudinaryTransformations'
import { isPrivatePhotoUrl } from '../components/PhotoBank/PhotoBank.utils'
import { CROP_TYPE } from './cropUtils.consts'
import { CROP_ALIGNMENT, type CropAlignment } from './cropAlignment.consts'
import { fitCropToAllRatios } from './cropRatioUtils'
import type { Crop, Rect } from './cropUtils.types'
import type {
  Photo,
  PhotoFaceData,
} from '../components/PhotoBank/PhotoBank.types'
import type { Tile } from '../components/TilesProvider/TilesProvider.types'
import type { TileCrop } from '../stores/tilesStore/tiles/tileSchemas'

const SMART_CROP_TIMEOUT = 2500

/**
 * Calculates the CSS properties for the crop rect to be positioned and scaled correctly in the frame.
 */
export function calculateTransformForCrop({
  domFrameDimensions,
  domImageDimensions,
  relativeCropRect,
}: {
  domFrameDimensions: Pick<Rect, 'width' | 'height'>
  domImageDimensions: Pick<Rect, 'width' | 'height'>
  relativeCropRect: Crop
}) {
  // relativeCropRect contains values between 0 and 1, it allows us to translate the crop to any image size.
  const domImageCropRect = {
    width: domImageDimensions.width * relativeCropRect.width,
    height: domImageDimensions.height * relativeCropRect.height,
    x: domImageDimensions.width * relativeCropRect.x,
    y: domImageDimensions.height * relativeCropRect.y,
  }

  const scaleX = domFrameDimensions.width / domImageCropRect.width
  const scaleY = domFrameDimensions.height / domImageCropRect.height

  return {
    transformOrigin: 'top left',
    transform: `scale(${scaleX}, ${scaleY}) translate(-${domImageCropRect.x}px, -${domImageCropRect.y}px)`,
  }
}

const CROP_ALIGNMENT_OFFSET_FACTOR: Record<
  CropAlignment,
  { x: number; y: number }
> = {
  [CROP_ALIGNMENT.CENTER]: { x: 0.5, y: 0.5 },
  [CROP_ALIGNMENT.LEFT]: { x: 0, y: 0.5 },
  [CROP_ALIGNMENT.RIGHT]: { x: 1, y: 0.5 },
  [CROP_ALIGNMENT.TOP]: { x: 0.5, y: 0 },
  [CROP_ALIGNMENT.BOTTOM]: { x: 0.5, y: 1 },
}

export function calcImageDefaultCrop(
  imageWidth: number,
  imageHeight: number,
  targetRatio: number,
  alignment: CropAlignment = CROP_ALIGNMENT.CENTER
) {
  if (!imageWidth || !imageHeight) {
    logger.info(
      'calcImageDefaultCrop: Invalid image width or height, returning null',
      { level: 'error', extra: { imageWidth, imageHeight, targetRatio } }
    )
    return null
  }
  const sourceRatio = imageWidth / imageHeight

  let width
  let height
  if (sourceRatio >= targetRatio) {
    width = imageWidth * (targetRatio / sourceRatio)
    height = imageHeight
  } else {
    width = imageWidth
    height = imageHeight * (sourceRatio / targetRatio)
  }
  const offsetFactor = CROP_ALIGNMENT_OFFSET_FACTOR[alignment]
  const roundedWidth = Math.round(width)
  const roundedHeight = Math.round(height)

  return {
    width: roundedWidth,
    height: roundedHeight,
    x: Math.round((imageWidth - roundedWidth) * offsetFactor.x),
    y: Math.round((imageHeight - roundedHeight) * offsetFactor.y),
    cropType: CROP_TYPE.DEFAULT,
  }
}

export function normalizeCropParams(
  cropParams: Rect & { cropType?: Crop['cropType'] },
  imageDimensions: Pick<Rect, 'width' | 'height'>
) {
  return {
    x: cropParams.x / imageDimensions.width,
    y: cropParams.y / imageDimensions.height,
    width: cropParams.width / imageDimensions.width,
    height: cropParams.height / imageDimensions.height,
    cropType: cropParams.cropType ?? CROP_TYPE.DEFAULT,
  }
}

function cropImageOnCanvas(image: CanvasImageSource, crop: Rect) {
  const canvas = document.createElement('canvas')
  canvas.width = crop.width
  canvas.height = crop.height

  const ctx = canvas.getContext('2d')
  ctx?.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    canvas.width,
    canvas.height
  )
  return canvas
}

const CROPPED_IMAGE_CACHE: Record<string, any> = {}

export function cropImagePromise(
  image: HTMLImageElement,
  crop: Crop,
  includeBlob: boolean = false
) {
  if (!image.src || image.src.length > 100) {
    logger.warning('cropImagePromise: Invalid image src', image.src)
  }
  const cacheKey = `${image.src}_${crop.x}_${crop.y}_${crop.width}_${crop.height}`
  if (CROPPED_IMAGE_CACHE[cacheKey]) {
    return CROPPED_IMAGE_CACHE[cacheKey]
  }

  const canvas = cropImageOnCanvas(image, crop)
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      try {
        const url = URL.createObjectURL(blob!)
        releaseCanvas(canvas)
        CROPPED_IMAGE_CACHE[cacheKey] = url
        resolve(includeBlob ? { url, blob } : url)
      } catch (error) {
        logger.error('Failed cropping image', error)
        reject(error)
      }
    })
  })
}

export function scaleCropParams(cropParams: Rect, scale: number) {
  return {
    x: Math.round(cropParams.x * scale),
    y: Math.round(cropParams.y * scale),
    width: Math.round(cropParams.width * scale),
    height: Math.round(cropParams.height * scale),
  }
}

export async function calculateCropForRatio({
  targetRatio,
  photo,
}: {
  targetRatio: number
  photo: Photo & { cropParams: Record<number, Crop> }
}): Promise<TileCrop | null> {
  const {
    width,
    height,
    thumbDimensions,
    lowResUrl,
    url,
    isLocal,
    faces,
    cropParams = {},
  } = photo
  let newCropParams: Crop | undefined
  try {
    const didUserCrop =
      Object.entries(cropParams).length > 0 &&
      [CROP_TYPE.MANUAL, CROP_TYPE.FIT].includes(
        Object.values(cropParams)[0].cropType ?? CROP_TYPE.DEFAULT
      )
    if (didUserCrop) {
      const [ratioToAdjustTo, cropToAdjustTo] = Object.entries(cropParams)[0]
      newCropParams = fitCropToAllRatios({
        ratiosToCalculate: [targetRatio],
        cropRect: cropToAdjustTo,
        currentAspectRatio: ratioToAdjustTo,
        imageWidth: width,
        imageHeight: height,
      })[targetRatio]
    } else {
      // Calculate cropParams by smart crop and scale them to fit the original image
      if (isLocal) {
        const smartCropResult = await calculateSmartCropFromUrl({
          imageUrl: lowResUrl,
          targetAspectRatio: targetRatio,
          faces,
          timeout: SMART_CROP_TIMEOUT,
        })
        newCropParams = scaleCropParams(
          smartCropResult.cropRect,
          width / thumbDimensions.low.width
        )
      } else if (lowResUrl) {
        const smartCropResult = await calculateSmartCropFromUrl({
          imageUrl: lowResUrl,
          targetAspectRatio: targetRatio,
          timeout: SMART_CROP_TIMEOUT,
          faces,
        })
        // A private photo's lowResUrl is its minimal preview, which carries no size in its URL
        const lowResWidth = isPrivatePhotoUrl(url)
          ? thumbDimensions.minimal.width
          : extractThumbDimensionsFromUrl(lowResUrl)?.width ?? 0
        newCropParams = scaleCropParams(
          smartCropResult.cropRect,
          width / lowResWidth
        )
      } else if (url && !isPrivatePhotoUrl(url)) {
        // No scale required because it's the original url
        const smartCropResult = await calculateSmartCropFromUrl({
          imageUrl: url,
          targetAspectRatio: targetRatio,
          timeout: SMART_CROP_TIMEOUT,
          faces,
        })
        newCropParams = smartCropResult.cropRect
      }
    }
  } catch (e) {
    if (!(e instanceof ExternalError)) {
      logger.error('Failed to calculate smart crops for photo', e)
    }
  }

  const crop = newCropParams || calcImageDefaultCrop(width, height, targetRatio)
  return crop && { zoom: 1, ...crop }
}

const cropChangeThreshold = 10
const cropResultProps = ['width', 'height', 'x', 'y'] as const

export function didCropChange(cropA: Rect, cropB: Rect) {
  return cropResultProps.some((key) => {
    const a = cropA[key]
    const b = cropB[key]

    // We only count a crop if the change in any property is above the threshold
    // This is done because the cropping lib sometimes slight changes the crop without user interaction
    const distance = Math.abs(a - b)
    return distance > cropChangeThreshold
  })
}

export function getInitialCroppedArea(tile: Tile) {
  const { width, height, x, y, cropType } =
    tile.cropParams[tile.currentAspectRatio] || {}
  return { width, height, x, y, cropType }
}

export function getCropRelevantFaces(
  faces: PhotoFaceData[] | undefined
): PhotoFaceData[] {
  return faces?.filter((f) => !f.belowCropThreshold) ?? []
}
